import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { FiPlus, FiRefreshCw, FiX } from "react-icons/fi";
import {
  createPermission,
  fetchPermissions,
} from "../../../redux/actions/role-management/permission/permissionActions";

const Permission = () => {
  const dispatch = useDispatch();
  const { loading, permissions } = useSelector(
    (state) => state.permission || { loading: false, permissions: [] },
  );
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    permission_name: "",
    description: "",
  });

  useEffect(() => {
    dispatch(fetchPermissions());
  }, [dispatch]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.permission_name.trim()) {
      toast.error("Permission name is required");
      return;
    }

    try {
      const result = await dispatch(createPermission(formData));
      if (result?.meta?.status) {
        setShowModal(false);
        setFormData({ permission_name: "", description: "" });
        dispatch(fetchPermissions());
      }
    } catch (error) {
      // Error handled in action
    }
  };

  const handleRefresh = () => {
    dispatch(fetchPermissions());
    toast.success("Data refreshed!");
  };

  return (
    <div className="p-4 md:p-6 w-full">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold uppercase text-gray-900 dark:text-white">
            Permissions
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage system permissions
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
          >
            <FiRefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Add Permission
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Permissions List
          </h2>
          <span className="text-sm font-medium text-orange-800 dark:text-orange-200 border border-orange-200 dark:border-orange-700 px-3 py-1 rounded-md">
            TOTAL: {permissions.length || 0}
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-orange-600 dark:border-orange-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : permissions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                    #
                  </th>
                  <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                    Permission Name
                  </th>
                  <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                    Description
                  </th>
                  <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                    Created At
                  </th>
                </tr>
              </thead>
              <tbody>
                {permissions.map((item, index) => (
                  <tr
                    key={item.id}
                    className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">
                      {index + 1}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                      {item.permission_name}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                      {item.description || "N/A"}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString("en-IN")
                        : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              No permissions found
            </p>
          </div>
        )}
      </div>

      {/* Add Permission Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Add Permission
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Permission Name *
                </label>
                <input
                  type="text"
                  name="permission_name"
                  value={formData.permission_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter permission name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter description"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? "Creating..." : "Create Permission"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Permission;
